import type { ProductInfoData } from "@/components/ui/product-info-modal"
import { blobMediaUrl } from "@/lib/blob-url"

/**
 * The blood tests only the membership-first site sells, the booking's add-ons
 * of 29.09 (Eugene, 01.10: "all under the flag, on the new site"). The same
 * shape as `BLOOD_ADDONS`, kept apart so today's site never lists them.
 * Their pages are in `data/tests.ts` (MEMBERSHIP_FIRST_ONLY_TEST_SLUGS) and
 * their prices come from `get-products` like every add-on (lib/pricing.ts).
 * The picture is the green tube the nutrient tests carry (Eugene, 02.10);
 * `navKey` is the nav's short name (`nav.*`).
 */
export const MEMBERSHIP_FIRST_BLOOD_ADDONS = [
  {
    titleKey: "addonCoeliac" as const,
    navKey: "coeliacShort" as const,
    descKey: "addonCoeliacDesc" as const,
    imageSrc: blobMediaUrl("/images/addons/tubes/tube-nutrients.webp"),
    slug: "coeliac-blood-test",
  },
  {
    titleKey: "addonHeavyMetals" as const,
    navKey: "heavyMetalsShort" as const,
    descKey: "addonHeavyMetalsDesc" as const,
    imageSrc: blobMediaUrl("/images/addons/tubes/tube-nutrients.webp"),
    slug: "heavy-metals-blood-test",
  },
  {
    titleKey: "addonTraceElements" as const,
    navKey: "traceElementsShort" as const,
    descKey: "addonTraceElementsDesc" as const,
    imageSrc: blobMediaUrl("/images/addons/tubes/tube-nutrients.webp"),
    slug: "trace-elements-blood-test",
  },
]

const WHAT_YOU_GET = [
  "piInsightsByPhysicians",
  "piDetailedReport",
  "piPersonalActionPlan",
  "piAddTestsMriAnytime",
]

/** Their "More info" pop-ups, as `ADDON_INFO` (keys in `pricing`). */
export const MEMBERSHIP_FIRST_ADDON_INFO: Record<string, ProductInfoData> = {
  addonCoeliac: {
    nameKey: "addonCoeliac",
    image: blobMediaUrl("/images/addons/tubes/tube-nutrients.webp"),
    priceKey: "addonCoeliac",
    descKey: "infoCoeliacDesc",
    benefitKeys: ["piAddonCoeliacBenefit1"],
    audienceKeys: ["piAddonCoeliacAudience1"],
    whatYouGetKeys: WHAT_YOU_GET,
    infoKeys: {
      healthAreas: "piCoeliacInfoHealthAreas",
      timeNeeded: "piBloodDrawNoExtraTime",
      radiation: "piNoneAtAll",
      preparation: "piCoeliacInfoPreparation",
      forWhom: "piAdults18plus",
    },
  },
  addonHeavyMetals: {
    nameKey: "addonHeavyMetals",
    image: blobMediaUrl("/images/addons/tubes/tube-nutrients.webp"),
    priceKey: "addonHeavyMetals",
    descKey: "infoHeavyMetalsDesc",
    benefitKeys: ["piAddonHeavyMetalsBenefit1"],
    audienceKeys: ["piAddonHeavyMetalsAudience1"],
    whatYouGetKeys: WHAT_YOU_GET,
    infoKeys: {
      healthAreas: "piHeavyMetalsInfoHealthAreas",
      timeNeeded: "piBloodDrawNoExtraTime",
      radiation: "piNoneAtAll",
      preparation: "piHeavyMetalsInfoPreparation",
      forWhom: "piAdults18plus",
    },
  },
  addonTraceElements: {
    nameKey: "addonTraceElements",
    image: blobMediaUrl("/images/addons/tubes/tube-nutrients.webp"),
    priceKey: "addonTraceElements",
    descKey: "infoTraceElementsDesc",
    benefitKeys: ["piAddonTraceElementsBenefit1"],
    audienceKeys: ["piAddonTraceElementsAudience1"],
    whatYouGetKeys: WHAT_YOU_GET,
    infoKeys: {
      healthAreas: "piTraceElementsInfoHealthAreas",
      timeNeeded: "piBloodDrawNoExtraTime",
      radiation: "piNoneAtAll",
      preparation: "piTraceElementsInfoPreparation",
      forWhom: "piAdults18plus",
    },
  },
}

/**
 * Their biomarkers, as the booking lists them. They are not in the CMS, so
 * the pop-up reads these keys (in `pricing`) in place of a CMS package.
 */
export const MEMBERSHIP_FIRST_BIOMARKERS: Record<
  string,
  { categoryKey: string; itemKeys: string[] }
> = {
  addonCoeliac: {
    categoryKey: "piBioCoeliacDisease",
    itemKeys: ["piBioAntiTtgIga", "piBioTotalIga"],
  },
  addonHeavyMetals: {
    categoryKey: "piBioHeavyMetals",
    itemKeys: ["piBioLead", "piBioMercury"],
  },
  addonTraceElements: {
    categoryKey: "piBioTraceElements",
    itemKeys: [
      "piBioZinc",
      "piBioSelenium",
      "piBioCopper",
      "piBioCopperZincRatio",
    ],
  },
}
